import React from 'react';

// Client-facing read-only view of a compliance proposal.
//
// A compliance client_path covers four kinds (stage_data.compliance.kind):
//   - 'sra'         — HIPAA Security Risk Assessment (one-time, itemized)
//   - 'remediation' — Compliance Remediation (one-time, itemized; lines with
//                     capital:true are broken out as capital equipment)
//   - 'monitoring'  — Compliance Monitoring (monthly recurring)
//   - 'tracking'    — Assessment Remediation Tracking (monthly recurring)
//
// One-time kinds render `data.line_items` with per-line discounts. Line math
// mirrors backend/client_path/compliance.py line_total(): qty x unit_price,
// minus discount_pct percent, minus flat discount, floored at 0. Both invoice
// the FULL discounted amount on acceptance (like hardware — no deposit split).
//
// Recurring kinds don't bill from this proposal — they go through the
// contract -> rate-sheet flow — so they get a plain-English description and a
// note that the monthly amount is finalized on accept.
//
// Props: `data` is the proposal object from GET /api/portal/proposals/{id}
// (response.proposal === stage_data.proposal). `compliance` is optional
// (stage_data.compliance, if the caller has it); otherwise the kind is read
// from data._compliance_kind. Defaults to 'sra' like the backend.

const money = (v) =>
  `$${(Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const round2 = (n) => Math.round(n * 100) / 100;

// Mirrors backend client_path/compliance.py line_total(): base, then percent
// off, then flat amount off, floored at zero.
const lineBase = (r) => (Number(r.quantity) || 0) * (Number(r.unit_price) || 0);
const lineTotal = (r) => {
  let base = lineBase(r);
  const pct = Number(r.discount_pct) || 0;
  if (pct) base *= (1 - pct / 100);
  const amt = Number(r.discount) || 0;
  if (amt) base -= amt;
  return round2(Math.max(base, 0));
};
const hasDiscount = (r) => (Number(r.discount_pct) || 0) > 0 || (Number(r.discount) || 0) > 0;

const KIND_TITLES = {
  sra: 'HIPAA Security Risk Assessment',
  remediation: 'Compliance Remediation',
  monitoring: 'Compliance Monitoring',
  tracking: 'Assessment Remediation Tracking',
};

const RECURRING_BLURBS = {
  monitoring:
    'Ongoing compliance monitoring for your organization. We keep continuous watch over ' +
    'the safeguards that protect your patient and business data — reviewing security ' +
    'settings, tracking required policies and training, and alerting you when something ' +
    'needs attention — so you stay compliant year-round instead of scrambling at audit time.',
  tracking:
    'Structured follow-through on the findings from your security risk assessment. We ' +
    'track each remediation item to completion, keep the documentation regulators expect ' +
    'to see, and give you a clear picture of what is done and what is still open.',
};

const cell = { padding: '8px 10px', borderBottom: '1px solid #eef0f3', fontSize: 14 };
const groupHeader = {
  ...cell,
  background: '#f8fafc',
  fontWeight: 600,
  fontSize: 13,
  color: '#475569',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
};

function discountNote(r) {
  const parts = [];
  const pct = Number(r.discount_pct) || 0;
  const amt = Number(r.discount) || 0;
  if (pct) parts.push(`−${pct}%`);
  if (amt) parts.push(`−${money(amt)}`);
  return parts.join(' / ');
}

function ItemRows({ rows, showDiscount }) {
  return rows.map((r, i) => (
    <tr key={i}>
      <td style={cell}>{r.description}</td>
      <td style={{ ...cell, textAlign: 'right' }}>{r.quantity}</td>
      <td style={{ ...cell, textAlign: 'right' }}>{money(r.unit_price)}</td>
      {showDiscount && (
        <td style={{ ...cell, textAlign: 'right', color: '#065f46' }}>
          {hasDiscount(r) ? (
            <>
              <span style={{ textDecoration: 'line-through', color: '#94a3b8', marginRight: 6 }}>
                {money(lineBase(r))}
              </span>
              {discountNote(r)}
            </>
          ) : (
            '—'
          )}
        </td>
      )}
      <td style={{ ...cell, textAlign: 'right' }}>{money(lineTotal(r))}</td>
    </tr>
  ));
}

const TRACK_LABELS = {
  add_on: 'Add-on to your managed IT services',
  standalone: 'Standalone compliance monitoring',
  hosting_bundle: 'Hosting bundle (we hold your BAA)',
};

// Itemized MONTHLY monitoring pricing — rendered when the proposal carries a
// compliance_monitoring block (track-priced, per-line discounts, hard floor
// applied after discounts). Falls back to the plain recurring blurb when the
// staff proposal predates the monitoring builder.
function MonitoringSummary({ data }) {
  const mon = data?.compliance_monitoring || {};
  const rows = Array.isArray(mon.line_items) ? mon.line_items : [];
  const showDiscount = rows.some(hasDiscount);
  const subtotal = round2(rows.reduce((s, r) => s + lineTotal(r), 0));
  const floor = Number(mon.min_monthly) || 0;
  const topup = floor > 0 && subtotal < floor ? round2(floor - subtotal) : 0;
  const total = round2(subtotal + topup);

  return (
    <div>
      <h3 style={{ margin: '0 0 10px', fontSize: 17 }}>{KIND_TITLES.monitoring}</h3>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '14px 16px', marginBottom: 14 }}>
        <p style={{ margin: 0, fontSize: 14, color: '#334155', lineHeight: 1.6 }}>
          {RECURRING_BLURBS.monitoring}
        </p>
        {mon.remediation_tracking && (
          <p style={{ margin: '10px 0 0', fontSize: 14, color: '#334155', lineHeight: 1.6 }}>
            <strong>Includes Assessment Remediation Tracking:</strong>{' '}
            every remediation item from your assessment is tracked to closure,
            with the documentation regulators expect to see.
          </p>
        )}
      </div>
      {TRACK_LABELS[mon.track] && (
        <p style={{ margin: '0 0 10px', fontSize: 13, color: '#64748b' }}>
          Plan: <strong>{TRACK_LABELS[mon.track]}</strong>
          {(Number(mon.endpoint_count) || 0) > 0 && <> · {mon.endpoint_count} endpoints</>}
        </p>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={cell}>Description</th>
            <th style={{ ...cell, textAlign: 'right' }}>Qty</th>
            <th style={{ ...cell, textAlign: 'right' }}>Unit / mo</th>
            {showDiscount && <th style={{ ...cell, textAlign: 'right' }}>Discount</th>}
            <th style={{ ...cell, textAlign: 'right' }}>Amount / mo</th>
          </tr>
        </thead>
        <tbody>
          <ItemRows rows={rows} showDiscount={showDiscount} />
        </tbody>
      </table>
      <div style={{ marginTop: 16, maxWidth: 380, marginLeft: 'auto' }}>
        {topup > 0 && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', color: '#475569', fontSize: 14 }}>
              <span>Monthly subtotal</span><span>{money(subtotal)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', color: '#475569', fontSize: 14 }}>
              <span>Plan minimum ({money(floor)}/mo)</span><span>{money(topup)}</span>
            </div>
          </>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 15 }}>
          <span>Monthly total</span><strong>{money(total)}/mo</strong>
        </div>
      </div>
      {data?.notes && (
        <p style={{ marginTop: 14, color: '#475569', fontSize: 14 }}><strong>Notes:</strong> {data.notes}</p>
      )}
      <p style={{ marginTop: 14, fontSize: 13, color: '#64748b' }}>
        This is a <strong>monthly service</strong>. Accepting takes you to your
        service agreement — there is nothing due today.
      </p>
    </div>
  );
}

export default function ComplianceProposalSummary({ data, compliance }) {
  const kind = compliance?.kind || data?._compliance_kind || 'sra';
  const title = KIND_TITLES[kind] || KIND_TITLES.sra;
  const isRecurring = kind === 'monitoring' || kind === 'tracking';

  // Monitoring with itemized track pricing gets the full monthly table.
  if (kind === 'monitoring' && (data?.compliance_monitoring?.line_items || []).length > 0) {
    return <MonitoringSummary data={data} />;
  }

  if (isRecurring) {
    return (
      <div>
        <h3 style={{ margin: '0 0 10px', fontSize: 17 }}>{title}</h3>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '14px 16px' }}>
          <p style={{ margin: 0, fontSize: 14, color: '#334155', lineHeight: 1.6 }}>
            {RECURRING_BLURBS[kind]}
          </p>
        </div>
        {data?.notes && (
          <p style={{ marginTop: 14, color: '#475569', fontSize: 14 }}><strong>Notes:</strong> {data.notes}</p>
        )}
        <p style={{ marginTop: 14, fontSize: 13, color: '#64748b' }}>
          This is a <strong>monthly service</strong>. Your exact monthly amount is confirmed
          with your service agreement when you accept — there is nothing due today.
        </p>
      </div>
    );
  }

  // One-time kinds (sra, remediation): itemized table, full amount invoiced
  // on acceptance.
  const rows = Array.isArray(data?.line_items) ? data.line_items : [];
  if (!rows.length) {
    return <p style={{ color: '#64748b' }}>This proposal has no line items yet.</p>;
  }

  const showDiscount = rows.some(hasDiscount);
  const total = round2(rows.reduce((s, r) => s + lineTotal(r), 0));

  // Remediation proposals can carry capital equipment (server, workstations,
  // network gear) that gets broken out from the remediation work itself.
  const capitalRows = kind === 'remediation' ? rows.filter((r) => r.capital === true) : [];
  const serviceRows = kind === 'remediation' ? rows.filter((r) => r.capital !== true) : rows;
  const splitCapital = capitalRows.length > 0;
  const serviceSubtotal = round2(serviceRows.reduce((s, r) => s + lineTotal(r), 0));
  const capitalSubtotal = round2(capitalRows.reduce((s, r) => s + lineTotal(r), 0));
  const colCount = showDiscount ? 5 : 4;

  return (
    <div>
      <h3 style={{ margin: '0 0 10px', fontSize: 17 }}>{title}</h3>
      {kind === 'remediation' && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
          <p style={{ margin: 0, fontSize: 14, color: '#78350f', lineHeight: 1.6 }}>
            <strong>One scope, one outcome.</strong>{' '}
            These items are the gaps between you and a clean audit — not
            optional add-ons. The scope is priced as a whole; equipment
            purchases are broken out below so you can see exactly where the
            dollars go.
          </p>
        </div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={cell}>Description</th>
            <th style={{ ...cell, textAlign: 'right' }}>Qty</th>
            <th style={{ ...cell, textAlign: 'right' }}>Unit</th>
            {showDiscount && <th style={{ ...cell, textAlign: 'right' }}>Discount</th>}
            <th style={{ ...cell, textAlign: 'right' }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {splitCapital ? (
            <>
              <tr><td style={groupHeader} colSpan={colCount}>Remediation Services</td></tr>
              <ItemRows rows={serviceRows} showDiscount={showDiscount} />
              <tr><td style={groupHeader} colSpan={colCount}>Capital Equipment</td></tr>
              <ItemRows rows={capitalRows} showDiscount={showDiscount} />
            </>
          ) : (
            <ItemRows rows={serviceRows} showDiscount={showDiscount} />
          )}
        </tbody>
      </table>

      <div style={{ marginTop: 16, maxWidth: 360, marginLeft: 'auto' }}>
        {splitCapital && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', color: '#475569', fontSize: 14 }}>
              <span>Remediation services</span><span>{money(serviceSubtotal)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', color: '#475569', fontSize: 14 }}>
              <span>Capital equipment</span><span>{money(capitalSubtotal)}</span>
            </div>
          </>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 15 }}>
          <span>Total</span><strong>{money(total)}</strong>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', color: '#065f46', fontSize: 15 }}>
          <span>Due on acceptance</span><strong>{money(total)}</strong>
        </div>
      </div>

      {data?.notes && (
        <p style={{ marginTop: 14, color: '#475569', fontSize: 14 }}><strong>Notes:</strong> {data.notes}</p>
      )}

      <p style={{ marginTop: 14, fontSize: 13, color: '#64748b' }}>
        Accepting this proposal generates an <strong>invoice for the full amount ({money(total)})</strong>.
      </p>
    </div>
  );
}
